import type { ResourceBlock, ResourceSection } from '@domain/schemas/contract.schema';
import { getSortedEntries } from '@root/helpers';

function getSortedBlocksForSection(section: ResourceSection): ResourceBlock[] {
  return getSortedEntries(section.layout, section.blocks);
}

function calculateCrossSectionInsertIndex(targetSection: ResourceSection, overId: string): number {
  const sortedBlocks = getSortedBlocksForSection(targetSection);
  const overBlockIndex = sortedBlocks.findIndex((b) => b.id === overId);

  // Insert AFTER the target block (overBlockIndex + 1) to match visual indicator
  return overBlockIndex !== -1 ? overBlockIndex + 1 : sortedBlocks.length;
}

function calculateSameSectionInsertIndex(targetSection: ResourceSection, overId: string): number {
  const sortedBlocks = getSortedBlocksForSection(targetSection);
  return sortedBlocks.findIndex((b) => b.id === overId);
}

function removeBlockFromSection(section: ResourceSection, blockId: string): ResourceSection {
  return {
    ...section,
    blocks: section.blocks.filter((b) => b.id !== blockId),
  };
}

function insertBlockIntoSection(section: ResourceSection, block: ResourceBlock, index: number): ResourceSection {
  return {
    ...section,
    blocks: [...section.blocks.slice(0, index), block, ...section.blocks.slice(index)],
  };
}

interface EntityWithId {
  id: string;
}

export function findSectionById<T extends EntityWithId = ResourceSection>(
  sections: T[],
  sectionId: string,
): T | undefined {
  return sections.find((s) => s.id === sectionId);
}

export function findBlockInSection(section: ResourceSection, blockId: string): ResourceBlock | undefined {
  return section.blocks.find((b) => b.id === blockId);
}

export function updateSectionsWithBlockMove(
  sections: ResourceSection[],
  sourceSectionId: string,
  targetSectionId: string,
  block: ResourceBlock,
  insertIndex: number,
): ResourceSection[] {
  return sections.map((section) => {
    if (section.id === sourceSectionId) {
      return removeBlockFromSection(section, block.id);
    }
    if (section.id === targetSectionId) {
      return insertBlockIntoSection(section, block, insertIndex);
    }
    return section;
  });
}

export function calculateDragOverInsertIndex(
  sections: ResourceSection[],
  originalSections: ResourceSection[],
  activeId: string,
  overId: string,
  overSectionId: string,
): number {
  if (overId === activeId) {
    // Block is hovering over itself - maintain current position
    const modifiedOverSection = findSectionById(sections, overSectionId);
    const currentIndex = modifiedOverSection?.blocks.findIndex((b) => b.id === activeId);
    const originalOverSection = findSectionById(originalSections, overSectionId);
    return currentIndex !== undefined && currentIndex !== -1 ? currentIndex : (originalOverSection?.blocks.length ?? 0);
  }

  // Block is hovering over a different block - insert AFTER target
  const originalOverSection = findSectionById(originalSections, overSectionId);
  if (!originalOverSection) return 0;

  const sortedBlocks = getSortedBlocksForSection(originalOverSection);
  const overBlockIndex = sortedBlocks.findIndex((b) => b.id === overId);
  return overBlockIndex !== -1 ? overBlockIndex + 1 : sortedBlocks.length;
}

export function determineTargetSectionId(
  dropTargetId: string,
  dropTargetType: string | undefined,
  dropTargetSectionId: string | undefined,
): string | undefined {
  if (dropTargetType === 'section') {
    return dropTargetId;
  }
  if (dropTargetType === 'block') {
    return dropTargetSectionId;
  }

  return dropTargetId;
}

export function determineCrossSectionDestination(
  targetSection: ResourceSection,
  dropTargetType: string | undefined,
  overId: string,
  activeId: string,
): number {
  if (dropTargetType === 'block' && overId !== activeId) {
    return calculateCrossSectionInsertIndex(targetSection, overId);
  }

  return 0;
}

export function determineWithinSectionDestination(targetSection: ResourceSection, overId: string): number {
  return calculateSameSectionInsertIndex(targetSection, overId);
}

export function findSectionForBlock(sections: ResourceSection[], blockId: string): string | undefined {
  return sections.find((s) => s.blocks.some((b) => b.id === blockId))?.id;
}
